import { usePlannerState } from '@hooks/usePlannerState';
import { useLeagueContext } from '@hooks/useLeagueContext';

const UNLIMITED_CHIPS: ReadonlySet<string> = new Set(['WILDCARD', 'FREE_HIT']);

export type UseFreeTransfersResult = {
  freeTransfers: number;
  remainingTransfers?: number;
  /**
   * Transfers already made this gameweek. Anything staged or suggested lands on
   * top of these, so a hit warning that ignores them under-reports the cost.
   */
  transfersMade: number;
  /**
   * Whether the gameweek's allowance was actually found. False means unknown,
   * not zero: `freeTransfers` reads 0 when there is no planner row for the
   * upcoming gameweek, and pricing a hit off that would invent a cost.
   */
  isAllowanceKnown: boolean;
  activeChip: string | null;
  isUnlimited: boolean;
  isReady: boolean;
};

export const useFreeTransfers = (): UseFreeTransfersResult => {
  const {
    context,
    isLoading: contextLoading,
    isError: contextError
  } = useLeagueContext();
  const {
    data: plannerData,
    isLoading: plannerLoading,
    isError: plannerError
  } = usePlannerState();

  const isReady =
    !contextLoading && !plannerLoading && !contextError && !plannerError;

  const gameweek = context?.nextGameweek?.number;
  const gameweekData = isReady
    ? plannerData?.data.gameweeks.find((gw) => gw.gameweek === gameweek)
    : undefined;

  const freeTransfers = gameweekData?.transfers.free ?? 0;
  const remainingTransfers = gameweekData?.transfers.remaining ?? undefined;
  const activeChip = gameweekData?.activeChip ?? null;

  // `made` is required by the spec but responses aren't validated at runtime,
  // so fall back to the allowance. That derivation is floored at zero and can
  // never exceed `free`, which is why `made` is preferred where present.
  const transfersMade =
    gameweekData?.transfers.made ??
    Math.max(0, freeTransfers - (remainingTransfers ?? freeTransfers));

  const isUnlimited =
    isReady &&
    (gameweekData?.transfers.free === null ||
      UNLIMITED_CHIPS.has(activeChip ?? ''));

  return {
    freeTransfers,
    remainingTransfers,
    transfersMade,
    isAllowanceKnown: gameweekData !== undefined,
    activeChip,
    isUnlimited,
    isReady
  };
};
